import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import TaskReportModal from './tasks/TaskReportModal';
import TaskTicketList from './tasks/TaskTicketList';
import AccessKeysPanel from './assets/AccessKeysPanel';
import AssetMediaPanel from './assets/AssetMediaPanel';
import AssetTagsSection from './assets/AssetTagsSection';
import FieldReportMediaPanel from './captures/FieldReportMediaPanel';
import AssetModal from './assets/AssetModal';
import { fetchWithAuth } from '../auth/fetchWithAuth';
import { formatAddress } from '../../shared/utils/formatAddress';
import { formatDate } from '../../shared/utils/formatDate';
import PropTypes from 'prop-types';
import { TASK_ID_PARAM, ASSET_TAB_PARAM } from '../utils/openViewParams';
import { pickInstallerAccessKey } from '../utils/pickInstallerAccessKey';
import { API_URL } from '../../config/env';
import StatusPill from '../../shared/components/pill/StatusPill';
import CountPill from '../../shared/components/pill/CountPill';

export default function AssetDetailsView({
  asset,
  onNewTask,
  onUpdate,
  saving = false,
  updateError = null,
  openTaskReportTaskId = null,
  initialTab = 'history',
  onClearTaskUrl = null,
}) {
  const [, setSearchParams] = useSearchParams();
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [maintenanceTasks, setMaintenanceTasks] = useState([]);
  const [maintenanceLoading, setMaintenanceLoading] = useState(false);
  const [maintenanceError, setMaintenanceError] = useState(null);
  const [feedbackTasks, setFeedbackTasks] = useState([]);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [feedbackError, setFeedbackError] = useState(null);

  const { openMaintenanceTasks, closedMaintenanceTasks, lastMaintenanceAt } = useMemo(() => {
    const open = maintenanceTasks.filter((t) => !t.completed_at);
    const closed = maintenanceTasks.filter((t) => !!t.completed_at);
    let latest = null;
    for (const task of closed) {
      if (!task.completed_at) continue;
      const d = new Date(task.completed_at);
      if (Number.isNaN(d.getTime())) continue;
      if (!latest || d > latest) latest = d;
    }
    return { openMaintenanceTasks: open, closedMaintenanceTasks: closed, lastMaintenanceAt: latest };
  }, [maintenanceTasks]);

  const loadMaintenanceTasks = useCallback(async () => {
    if (!asset?.id) return;
    setMaintenanceLoading(true);
    setMaintenanceError(null);
    try {
      const res = await fetchWithAuth(
        `${API_URL}/v1/admin/tasks?asset_id=${asset.id}&status=all`
      );
      if (!res.ok) throw new Error(res.statusText);
      const data = await res.json();
      setMaintenanceTasks(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load maintenance tasks:', err);
      setMaintenanceError(err.message || 'Failed to load tasks');
      setMaintenanceTasks([]);
    } finally {
      setMaintenanceLoading(false);
    }
  }, [asset?.id]);

  const loadFeedbackTasks = useCallback(async () => {
    if (!asset?.id) return;
    setFeedbackLoading(true);
    setFeedbackError(null);
    try {
      const res = await fetchWithAuth(
        `${API_URL}/v1/admin/tasks?asset_id=${asset.id}&kind=feedback&status=all`
      );
      if (!res.ok) throw new Error(res.statusText);
      const data = await res.json();
      setFeedbackTasks(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load feedback tasks:', err);
      setFeedbackError(err.message || 'Failed to load feedback');
      setFeedbackTasks([]);
    } finally {
      setFeedbackLoading(false);
    }
  }, [asset?.id]);

  // Apply URL tab only when navigating to a different asset — not on every
  // initialTab prop change (e.g. clearing ?task_id= must not reset the tab).
  useEffect(() => {
    setActiveTab(initialTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync tab only on asset change, not URL param cleanup
  }, [asset?.id]);

  useEffect(() => {
    loadMaintenanceTasks();
  }, [loadMaintenanceTasks]);

  useEffect(() => {
    if (activeTab === 'feedback') {
      loadFeedbackTasks();
    }
  }, [activeTab, loadFeedbackTasks]);

  // Open only when the deep-link task id changes — not when showModal flips to false
  // (otherwise closing races with URL cleanup and reopens the same task).
  useEffect(() => {
    if (!openTaskReportTaskId) return;
    setSelectedTaskId(openTaskReportTaskId);
    setShowModal(true);
  }, [openTaskReportTaskId]);

  const handleTaskClick = (task) => {
    setSelectedTaskId(task.id);
    setShowModal(true);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set(TASK_ID_PARAM, String(task.id));
      next.set(ASSET_TAB_PARAM, activeTab);
      return next;
    }, { replace: true });
  };

  const installerAccessKey = useMemo(
    () => pickInstallerAccessKey(asset.access_keys),
    [asset.access_keys]
  );

  const handleInstallerView = () => {
    if (!installerAccessKey) return;
    window.open(
      `${window.location.origin}/assets/access_key/${installerAccessKey}`,
      '_blank',
      'noopener'
    );
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedTaskId(null);
    onClearTaskUrl?.();
  };

  return (
    <div className="container-fluid py-4 px-0" style={{ maxWidth: 1050 }}>
      {/* Header
          Three full-width buttons beside the name do not fit a phone: the name was squeezed to a
          word and the buttons wrapped under it anyway. Below `md` they collapse into a three-dot
          menu next to the name, and the Active pill moves off the heading onto its own line so
          the name gets the width. Above `md` nothing changes. */}
      <div className="d-flex align-items-center gap-2 mb-1">
        <h3 className="fw-bold mb-0 min-w-0 text-break">
          {asset.asset_name || 'Asset'}
          {asset.asset_ext_id && (
            <StatusPill tone="success" size="touch" className="ms-2 align-middle d-none d-md-inline-flex">
              Active
            </StatusPill>
          )}
        </h3>

        <div className="ms-auto d-none d-md-flex gap-2">
          <button className="btn btn-light border" onClick={() => setShowEditModal(true)}>
            <i className="bi bi-pencil me-2" />
            Edit
          </button>
          {installerAccessKey ? (
            <button className="btn btn-light border" onClick={handleInstallerView}>
              <i className="bi bi-wrench-adjustable-circle me-2" />
              Installer View
            </button>
          ) : (
            <span title="No active access key for this asset">
              <button className="btn btn-light border" disabled style={{ pointerEvents: 'none' }}>
                <i className="bi bi-wrench-adjustable-circle me-2" />
                Installer View
              </button>
            </span>
          )}
          <button className="btn btn-primary" onClick={onNewTask}>
            <i className="bi bi-plus-lg me-2" />
            Start New Task
          </button>
        </div>

        <div className="ms-auto d-flex d-md-none flex-shrink-0">
          <div className="dropdown">
            <button
              className="btn btn-light border"
              type="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
              title="Asset actions"
              aria-label="Asset actions"
            >
              <i className="bi bi-three-dots-vertical" />
            </button>
            <ul className="dropdown-menu dropdown-menu-end">
              <li>
                <button className="dropdown-item" onClick={() => setShowEditModal(true)}>
                  <i className="bi bi-pencil me-2" />
                  Edit
                </button>
              </li>
              <li>
                <button
                  className="dropdown-item"
                  onClick={handleInstallerView}
                  disabled={!installerAccessKey}
                  title={installerAccessKey ? undefined : 'No active access key for this asset'}
                >
                  <i className="bi bi-wrench-adjustable-circle me-2" />
                  Installer View
                </button>
              </li>
              <li>
                <button className="dropdown-item" onClick={onNewTask}>
                  <i className="bi bi-plus-lg me-2" />
                  Start New Task
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {asset.asset_ext_id && (
        <div className="d-md-none mb-1">
          <StatusPill tone="success" size="touch">
            Active
          </StatusPill>
        </div>
      )}
      <div className="text-muted mb-3" style={{ fontSize: '1.07rem' }}>
        {formatAddress(asset)}
      </div>
      {/* Top Info Cards */}
      <div className="row g-4 mb-4">
        <div className="col-lg-8">
          <div className="card p-3 h-100">
            <div className="row g-3">
              <div className="col-md-4">
                <div className="small text-muted mb-1">Make & Model</div>
                <div className="fw-semibold">{asset.asset_make} {asset.asset_model}</div>
              </div>
              <div className="col-md-4">
                <div className="small text-muted mb-1">Serial Number</div>
                <div className="fw-semibold">{asset.asset_serial_number || <span className="text-muted">—</span>}</div>
              </div>
              <div className="col-md-4">
                <div className="small text-muted mb-1">Warranty Until</div>
                <div className="fw-semibold">{asset.asset_warranty_end_date ? formatDate(asset.asset_warranty_end_date) : <span className="text-muted">—</span>}</div>
              </div>
              <div className="col-md-4">
                <div className="small text-muted mb-1">Installation Date</div>
                <div className="fw-semibold">{asset.created_at ? formatDate(asset.created_at) : <span className="text-muted">—</span>}</div>
              </div>
              <div className="col-md-4">
                <div className="small text-muted mb-1">Last Maintenance</div>
                <div className="fw-semibold">
                  {lastMaintenanceAt ? (
                    formatDate(lastMaintenanceAt)
                  ) : (
                    <span className="text-muted">—</span>
                  )}
                </div>
              </div>
              <div className="col-md-12">
                <AssetTagsSection
                  manualTagIds={asset.tags?.manual_tag_ids || []}
                  autoTagIds={asset.tags?.auto_tag_ids || []}
                  onUpdate={onUpdate}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Task Activity & Access Keys - Two Column Layout */}
      <div className="row g-4">
        {/* Task activity, media — middle column */}
        <div className="col-lg-8">
          <div className="card p-3">
            <ul className="nav nav-tabs mb-3" role="tablist">
              <li className="nav-item" role="presentation">
                <button
                  type="button"
                  className={`nav-link ${activeTab === 'history' ? 'active' : ''}`}
                  onClick={() => setActiveTab('history')}
                  role="tab"
                  aria-selected={activeTab === 'history'}
                >
                  Task Activity
                </button>
              </li>
              <li className="nav-item" role="presentation">
                <button
                  type="button"
                  className={`nav-link ${activeTab === 'feedback' ? 'active' : ''}`}
                  onClick={() => setActiveTab('feedback')}
                  role="tab"
                  aria-selected={activeTab === 'feedback'}
                >
                  <i className="bi bi-chat-dots me-1" />
                  Feedback
                  {feedbackTasks.length > 0 && (
                    <CountPill className="ms-2">{feedbackTasks.length}</CountPill>
                  )}
                </button>
              </li>
            </ul>

            {activeTab === 'history' && (
              <div>
                {maintenanceError && (
                  <div className="alert alert-danger py-2 small mb-2">{maintenanceError}</div>
                )}
                <div className="fw-semibold small text-muted mb-2">Open</div>
                <TaskTicketList
                  items={openMaintenanceTasks}
                  loading={maintenanceLoading}
                  emptyMessage="No open tasks"
                  hideFilter
                  hideAssetName
                  onItemClick={handleTaskClick}
                />
                <div className="fw-semibold small text-muted mb-2 mt-3">Completed</div>
                <TaskTicketList
                  items={closedMaintenanceTasks}
                  loading={maintenanceLoading}
                  emptyMessage="No completed tasks"
                  hideFilter
                  hideAssetName
                  onItemClick={handleTaskClick}
                />
              </div>
            )}

            {activeTab === 'feedback' && (
              <div>
                <p className="text-muted small mb-3">
                  Tasks created by visitors using a User Workflow QR code or by
                  service engineers running anonymous user flows. These do not
                  appear on the maintenance log.
                </p>
                <TaskTicketList
                  items={feedbackTasks}
                  loading={feedbackLoading}
                  error={feedbackError}
                  emptyMessage="No feedback yet."
                  hideFilter
                  hideAssetName
                  onItemClick={handleTaskClick}
                />
              </div>
            )}
          </div>

          <AssetMediaPanel assetId={asset.id} />
          <FieldReportMediaPanel assetId={asset.id} />
        </div>

        {/* Access Keys - Right Column */}
        <div className="col-lg-4">
          <AccessKeysPanel accessKeys={asset.access_keys || []} />
        </div>
      </div>

      {/* Task Report Modal */}
      <TaskReportModal
        show={showModal}
        onClose={handleModalClose}
        taskId={selectedTaskId}
      />

      {/* Edit Asset Modal */}
      <AssetModal
        show={showEditModal}
        onClose={() => setShowEditModal(false)}
        asset={asset}
        onSave={(assetData) => {
          onUpdate?.(assetData);
          setShowEditModal(false);
        }}
        saving={saving}
        error={updateError}
      />
    </div>
  );
}

AssetDetailsView.propTypes = {
  asset: PropTypes.object.isRequired,
  onNewTask: PropTypes.func,
  onUpdate: PropTypes.func,
  saving: PropTypes.bool,
  updateError: PropTypes.string,
  openTaskReportTaskId: PropTypes.number,
  initialTab: PropTypes.oneOf(['history', 'feedback']),
  onClearTaskUrl: PropTypes.func,
};
